/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { HYDRATE_DASHBOARD } from '../actions/hydrate';

export const APPLY_THEME = 'APPLY_THEME';
export const TOGGLE_CHART_THEME = 'TOGGLE_CHART_THEME';
export const TOGGLE_THEME_CONTROLLER = 'TOGGLE_THEME_CONTROLLER';
export const HIDE_THEME_CONTROLLER = 'HIDE_THEME_CONTROLLER';
export const RESET_LAYOUT_THEME = 'RESET_LAYOUT_THEME';

export interface FontProperties {
  color?: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textBorderColor?: string;
  textBorderWidth?: number;
  textShadowColor?: string;
  textShadowBlur?: number;
  textShadowOffsetX?: number;
  textShadowOffsetY?: number;
}

export interface TooltipProperties {
  color?: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  backgroundColor?: string;
  textBorderColor?: string;
  textBorderWidth?: number;
  textShadowColor?: string;
  textShadowBlur?: number;
  textShadowOffsetX?: number;
  textShadowOffsetY?: number;
}

export interface TableChartStyles {
  // Table container
  table?: {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: string;
    borderStyle?: string;
  };
  // Table header
  tableHeader?: {
    backgroundColor?: string;
    color?: string;
    fontSize?: string;
    fontFamily?: string;
    fontWeight?: string;
    fontStyle?: string;
    padding?: string;
    borderColor?: string;
    textAlign?: string;
  };
  // Table body cells
  tableCell?: {
    backgroundColor?: string;
    color?: string;
    fontSize?: string;
    fontFamily?: string;
    fontWeight?: string;
    fontStyle?: string;
    padding?: string;
    borderColor?: string;
  };
  // Table row hover
  tableRowHover?: {
    backgroundColor?: string;
  };
  // Metric cells (right-aligned numeric data)
  metricCell?: {
    color?: string;
    fontWeight?: string;
  };
  // Null values
  nullValue?: {
    color?: string;
    fontStyle?: string;
  };
  // Active filter cells
  activeFilter?: {
    backgroundColor?: string;
  };
  // Filter hover
  filterHover?: {
    backgroundColor?: string;
  };
  // Sorted column indicator
  sortedColumn?: {
    iconColor?: string;
  };
}

export interface MarkdownStyles {
  // Headings
  h1?: {
    fontSize?: string;
    fontWeight?: string;
    fontFamily?: string;
    fontStyle?: string;
    color?: string;
    marginTop?: string;
    marginBottom?: string;
    lineHeight?: string;
  };
  h2?: {
    fontSize?: string;
    fontWeight?: string;
    fontFamily?: string;
    fontStyle?: string;
    color?: string;
    marginTop?: string;
    marginBottom?: string;
    lineHeight?: string;
  };
  h3?: {
    fontSize?: string;
    fontWeight?: string;
    fontFamily?: string;
    fontStyle?: string;
    color?: string;
    marginTop?: string;
    marginBottom?: string;
    lineHeight?: string;
  };
  h4?: {
    fontSize?: string;
    fontWeight?: string;
    fontFamily?: string;
    fontStyle?: string;
    color?: string;
    marginTop?: string;
    marginBottom?: string;
    lineHeight?: string;
  };
  h5?: {
    fontSize?: string;
    fontWeight?: string;
    fontFamily?: string;
    fontStyle?: string;
    color?: string;
    marginTop?: string;
    marginBottom?: string;
    lineHeight?: string;
  };
  h6?: {
    fontSize?: string;
    fontWeight?: string;
    fontFamily?: string;
    fontStyle?: string;
    color?: string;
    marginTop?: string;
    marginBottom?: string;
    lineHeight?: string;
  };
  // Paragraph
  p?: {
    fontSize?: string;
    fontFamily?: string;
    fontStyle?: string;
    lineHeight?: string;
    color?: string;
    marginBottom?: string;
  };
  // Links
  a?: {
    color?: string;
    textDecoration?: string;
    hoverColor?: string;
    hoverTextDecoration?: string;
  };
  // Lists
  ul?: {
    marginLeft?: string;
    marginBottom?: string;
    listStyleType?: string;
    color?: string;
    fontFamily?: string;
    fontWeight?: string;
    fontStyle?: string;
  };
  ol?: {
    marginLeft?: string;
    marginBottom?: string;
    listStyleType?: string;
    color?: string;
    fontFamily?: string;
    fontWeight?: string;
    fontStyle?: string;
  };
  li?: {
    marginBottom?: string;
    lineHeight?: string;
    color?: string;
    fontFamily?: string;
    fontWeight?: string;
    fontStyle?: string;
  };
  // Code
  code?: {
    fontSize?: string;
    fontFamily?: string;
    backgroundColor?: string;
    color?: string;
    padding?: string;
    borderRadius?: string;
  };
  pre?: {
    fontSize?: string;
    fontFamily?: string;
    backgroundColor?: string;
    color?: string;
    padding?: string;
    borderRadius?: string;
    overflowX?: string;
    marginBottom?: string;
  };
  // Blockquote
  blockquote?: {
    fontSize?: string;
    fontStyle?: string;
    color?: string;
    borderLeft?: string;
    paddingLeft?: string;
    marginLeft?: string;
    marginBottom?: string;
  };
  // Table
  table?: {
    borderCollapse?: string;
    width?: string;
    marginBottom?: string;
  };
  th?: {
    backgroundColor?: string;
    color?: string;
    padding?: string;
    border?: string;
    fontWeight?: string;
    textAlign?: string;
  };
  td?: {
    padding?: string;
    border?: string;
    color?: string;
  };
  tr?: {
    hoverBackgroundColor?: string;
  };
  // Horizontal Rule
  hr?: {
    border?: string;
    marginTop?: string;
    marginBottom?: string;
  };
  // Strong and Em
  strong?: {
    fontWeight?: string;
    color?: string;
  };
  em?: {
    fontStyle?: string;
    color?: string;
  };
}

export interface LayoutThemingStyles {
  presetName?: string;
  theme?: {
    background: {
      primary: string;
      secondary: string;
      tertiary: string;
    };
    text: {
      primary: string;
    };
    border: {
      default: string;
      subtle: string;
    };
  };
}

export interface CustomThemeState {
  fontProperties: FontProperties;
  tooltipProperties: TooltipProperties;
  markdownStyles?: MarkdownStyles;
  tableChartStyles?: TableChartStyles;
  layoutThemingStyles?: LayoutThemingStyles | null;
  enabled: boolean;
  showThemeController: boolean;
}

export interface ApplyThemeAction {
  type: typeof APPLY_THEME;
  payload: CustomThemeState;
}

export interface ToggleChartThemeAction {
  type: typeof TOGGLE_CHART_THEME;
}

export interface ToggleThemeControllerAction {
  type: typeof TOGGLE_THEME_CONTROLLER;
}

export interface HideThemeControllerAction {
  type: typeof HIDE_THEME_CONTROLLER;
}

export interface ResetLayoutThemeAction {
  type: typeof RESET_LAYOUT_THEME;
}

export type CustomThemeActionTypes =
  | ApplyThemeAction
  | ToggleChartThemeAction
  | ToggleThemeControllerAction
  | HideThemeControllerAction
  | ResetLayoutThemeAction;

// Base font configuration (color will be taken from layoutThemingStyles.theme.text.primary)
const BASE_FONT: FontProperties = {
  fontFamily: 'Arial',
  fontSize: 15,
  fontWeight: 'normal',
  fontStyle: 'normal',
};

// Base tooltip configuration
const BASE_TOOLTIP: TooltipProperties = {
  color: '#000000',
  fontFamily: 'Arial',
  fontSize: 14,
  fontWeight: 'normal',
  fontStyle: 'normal',
  backgroundColor: '#ffffff',
};

// Base table chart configuration
const BASE_TABLE_CHART: TableChartStyles = {
  table: {
    backgroundColor: '#ffffff',
    borderColor: '#e5e7eb',
    borderWidth: '1px',
    borderStyle: 'solid',
  },
  tableHeader: {
    backgroundColor: '#f9fafb',
    color: '#111827',
    fontSize: '14px',
    fontFamily: 'Arial, sans-serif',
    fontWeight: '600',
    fontStyle: 'normal',
    padding: '12px',
    borderColor: '#e5e7eb',
    textAlign: 'left',
  },
  tableCell: {
    backgroundColor: '#ffffff',
    color: '#374151',
    fontSize: '14px',
    fontFamily: 'Arial, sans-serif',
    fontWeight: '400',
    fontStyle: 'normal',
    padding: '12px',
    borderColor: '#e5e7eb',
  },
  tableRowHover: { backgroundColor: '#f9fafb' },
  metricCell: { color: '#111827', fontWeight: '500' },
  nullValue: { color: '#9ca3af', fontStyle: 'italic' },
  activeFilter: { backgroundColor: '#dbeafe' },
  filterHover: { backgroundColor: '#eff6ff' },
  sortedColumn: { iconColor: '#111827' },
};

// Base layout theming configuration - null means no theme (use default Superset theme)
const BASE_LAYOUT_THEMING: LayoutThemingStyles | null = null;

// Base markdown configuration
const BASE_MARKDOWN: MarkdownStyles = {
  h1: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1f2937',
    marginTop: '24px',
    marginBottom: '16px',
    lineHeight: '1.25',
  },
  h2: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1f2937',
    marginTop: '20px',
    marginBottom: '12px',
    lineHeight: '1.3',
  },
  h3: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#374151',
    marginTop: '16px',
    marginBottom: '8px',
    lineHeight: '1.4',
  },
  h4: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151',
    marginTop: '16px',
    marginBottom: '8px',
    lineHeight: '1.4',
  },
  h5: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#4b5563',
    marginTop: '12px',
    marginBottom: '8px',
    lineHeight: '1.5',
  },
  h6: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#4b5563',
    marginTop: '12px',
    marginBottom: '8px',
    lineHeight: '1.5',
  },
  p: {
    fontSize: '14px',
    lineHeight: '1.75',
    color: '#374151',
    marginBottom: '16px',
  },
  a: {
    color: '#3b82f6',
    textDecoration: 'none',
    hoverColor: '#2563eb',
    hoverTextDecoration: 'underline',
  },
  ul: {
    marginLeft: '24px',
    marginBottom: '16px',
    listStyleType: 'disc',
    fontFamily: 'Arial, sans-serif',
    fontWeight: '400',
    fontStyle: 'normal',
  },
  ol: {
    marginLeft: '24px',
    marginBottom: '16px',
    listStyleType: 'decimal',
    fontFamily: 'Arial, sans-serif',
    fontWeight: '400',
    fontStyle: 'normal',
  },
  li: {
    marginBottom: '8px',
    lineHeight: '1.75',
    color: '#374151',
    fontFamily: 'Arial, sans-serif',
    fontWeight: '400',
    fontStyle: 'normal',
  },
  code: {
    fontSize: '13px',
    fontFamily: 'monospace',
    backgroundColor: '#f3f4f6',
    color: '#e11d48',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  pre: {
    fontSize: '13px',
    fontFamily: 'monospace',
    backgroundColor: '#1f2937',
    color: '#f9fafb',
    padding: '16px',
    borderRadius: '8px',
    overflowX: 'auto',
    marginBottom: '16px',
  },
  blockquote: {
    fontSize: '14px',
    fontStyle: 'italic',
    color: '#6b7280',
    borderLeft: '4px solid #d1d5db',
    paddingLeft: '16px',
    marginLeft: '0',
    marginBottom: '16px',
  },
  table: { borderCollapse: 'collapse', width: '100%', marginBottom: '16px' },
  th: {
    backgroundColor: '#f3f4f6',
    color: '#111827',
    padding: '12px',
    border: '1px solid #e5e7eb',
    fontWeight: '600',
    textAlign: 'left',
  },
  td: { padding: '12px', border: '1px solid #e5e7eb', color: '#374151' },
  tr: { hoverBackgroundColor: '#f9fafb' },
  hr: { border: '1px solid #e5e7eb', marginTop: '24px', marginBottom: '24px' },
  strong: { fontWeight: '700', color: '#111827' },
  em: { fontStyle: 'italic', color: '#374151' },
};

// Function to get default theme state
const getDefaultThemeState = (): CustomThemeState => ({
  fontProperties: BASE_FONT,
  tooltipProperties: BASE_TOOLTIP,
  markdownStyles: BASE_MARKDOWN,
  tableChartStyles: BASE_TABLE_CHART,
  layoutThemingStyles: BASE_LAYOUT_THEMING,
  enabled: true,
  showThemeController: false,
});

// Initial state
const initialState: CustomThemeState = getDefaultThemeState();

// Reducer
export default function customThemeReducer(
  state: CustomThemeState = initialState,
  action: any,
): CustomThemeState {
  switch (action.type) {
    case HYDRATE_DASHBOARD: {
      // Load custom theme from dashboard metadata if available
      const customTheme =
        action.data?.dashboardInfo?.metadata?.custom_theme || {};

      // If no custom theme saved, use fresh default state
      if (Object.keys(customTheme).length === 0) {
        return getDefaultThemeState();
      }

      // Merge saved theme with state, preserving base defaults for missing properties
      return {
        ...state,
        fontProperties: {
          ...BASE_FONT,
          ...(customTheme.fontProperties || {}),
        },
        tooltipProperties: {
          ...BASE_TOOLTIP,
          ...(customTheme.tooltipProperties || {}),
        },
        markdownStyles: customTheme.markdownStyles || state.markdownStyles,
        tableChartStyles:
          customTheme.tableChartStyles || state.tableChartStyles,
        layoutThemingStyles:
          customTheme.layoutThemingStyles || state.layoutThemingStyles,
        enabled: customTheme.enabled !== undefined ? customTheme.enabled : true,
        showThemeController: state.showThemeController, // Don't restore UI state
      };
    }

    case APPLY_THEME:
      console.log('[Redux customTheme] APPLY_THEME:', action.payload);
      return {
        ...state,
        ...action.payload,
      };

    case TOGGLE_CHART_THEME:
      console.log(
        '[Redux customTheme] TOGGLE_CHART_THEME, current enabled:',
        state.enabled,
      );
      return {
        ...state,
        enabled: !state.enabled,
        fontProperties: !state.enabled ? BASE_FONT : {},
        tooltipProperties: !state.enabled ? BASE_TOOLTIP : {},
      };

    case TOGGLE_THEME_CONTROLLER:
      console.log(
        '[Redux customTheme] TOGGLE_THEME_CONTROLLER, current show:',
        state.showThemeController,
      );
      return {
        ...state,
        showThemeController: !state.showThemeController,
      };

    case HIDE_THEME_CONTROLLER:
      return {
        ...state,
        showThemeController: false,
      };

    case RESET_LAYOUT_THEME:
      return {
        ...state,
        layoutThemingStyles: null,
      };

    default:
      return state;
  }
}

// Action creators
export const applyTheme = (theme: CustomThemeState): ApplyThemeAction => ({
  type: APPLY_THEME,
  payload: theme,
});

export const toggleChartTheme = (): ToggleChartThemeAction => ({
  type: TOGGLE_CHART_THEME,
});

export const toggleThemeController = (): ToggleThemeControllerAction => ({
  type: TOGGLE_THEME_CONTROLLER,
});

export const hideThemeController = (): HideThemeControllerAction => ({
  type: HIDE_THEME_CONTROLLER,
});

export const resetLayoutTheme = (): ResetLayoutThemeAction => ({
  type: RESET_LAYOUT_THEME,
});
